import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const SLOTS = (() => { const o = []; for (let h = 0; h < 24; h++) for (const m of [0, 30]) o.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`); return o; })();
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function ddMmmYyyy(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MONTHS[m - 1]} ${y}`;
}

export function maskToDays(mask) {
  if (mask == null) return null; // null = 7-day
  return DAYS.filter(([, i]) => mask & (1 << i)).map(([, i]) => i);
}

// AIRPORT — weekly working days + holidays. CONTROLLED: all edits are STAGED via callbacks
// (no immediate persistence). The AIRPORTS section commits everything atomically on one Save.
export function AirportPickupCell({ airport, days, holidays, onDaysChange, onHolidaysChange }) {
  const [newDate, setNewDate] = useState("");
  const configured = days !== null && days !== undefined;
  const list = holidays || [];

  const toggle = (i) => {
    const base = configured ? days : [0, 1, 2, 3, 4, 5, 6];
    onDaysChange(base.includes(i) ? base.filter((x) => x !== i) : [...base, i].sort());
  };
  const addHoliday = () => {
    if (!newDate) return;
    if (list.includes(newDate)) { toast.error("That holiday is already configured."); return; }
    onHolidaysChange([...list, newDate].sort());
    setNewDate("");
  };
  const removeHoliday = (iso) => onHolidaysChange(list.filter((x) => x !== iso));

  return (
    <div className="text-xs space-y-2" data-testid={`airport-pickup-${airport.code}`}>
      <div className="flex flex-wrap gap-1">
        {DAYS.map(([lbl, i]) => {
          const on = configured ? days.includes(i) : true;
          return (
            <button key={i} type="button" onClick={() => toggle(i)}
              className={`px-1.5 py-0.5 rounded border ${on ? "bg-[hsl(var(--primary)/0.12)] border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted))]"}`}
              data-testid={`airport-day-${airport.code}-${i}`}>{lbl}</button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        {!configured && <span className="text-[hsl(var(--muted))]">7-day (all)</span>}
        {configured && <button type="button" className="underline text-[hsl(var(--muted))]" onClick={() => onDaysChange(null)} data-testid={`airport-days-clear-${airport.code}`}>reset to 7-day</button>}
      </div>
      <div className="pt-1">
        <div className="flex items-center gap-1">
          <input type="date" className="input-field !py-0.5 !text-xs" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid={`airport-holiday-date-${airport.code}`} />
          <button type="button" className="underline" onClick={addHoliday} data-testid={`airport-holiday-add-${airport.code}`}>+ Add Holiday</button>
        </div>
        <ul className="mt-1 space-y-0.5" data-testid={`airport-holiday-list-${airport.code}`}>
          {list.map((iso) => (
            <li key={iso} className="flex items-center gap-2">
              <span>{ddMmmYyyy(iso)}</span>
              <button type="button" className="text-red-600 underline" onClick={() => removeHoliday(iso)} data-testid={`airport-holiday-remove-${airport.code}-${iso}`}>Remove</button>
            </li>
          ))}
          {list.length === 0 && <li className="text-[hsl(var(--muted))]">No holidays</li>}
        </ul>
      </div>
    </div>
  );
}

// ROUTE — pickup opening/closing + same-day cutoff.
export function RoutePickupCell({ route }) {
  const [open, setOpen] = useState(route.pickup_open_time || "");
  const [close, setClose] = useState(route.pickup_close_time || "");
  const [cutoff, setCutoff] = useState(route.same_day_cutoff_time || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/routes/${route.route_code}/pickup-hours`, {
        pickup_open_time: open || null, pickup_close_time: close || null, same_day_cutoff_time: cutoff || null,
      });
      toast.success(`${route.route_code} pickup hours updated`);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.displayMessage || "Save failed"); }
    finally { setBusy(false); }
  };
  const clearHours = () => { setOpen(""); setClose(""); };
  const sel = (val, set, testid) => (
    <select className="input-field !py-0.5 !text-xs w-[90px]" value={val} onChange={(e) => set(e.target.value)} data-testid={testid}>
      <option value="">—</option>
      {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );

  return (
    <div className="text-xs space-y-1" data-testid={`route-pickup-${route.route_code}`}>
      <div className="flex items-center gap-1">Open {sel(open, setOpen, `route-open-${route.route_code}`)} Close {sel(close, setClose, `route-close-${route.route_code}`)}</div>
      <div className="flex items-center gap-1">Cutoff {sel(cutoff, setCutoff, `route-cutoff-${route.route_code}`)}</div>
      {!open && !close && <div className="text-[hsl(var(--muted))]">24×7 pickup{cutoff ? "" : " · no cutoff"}</div>}
      <div className="flex items-center gap-2">
        <button type="button" className="underline text-[hsl(var(--muted))]" onClick={clearHours} data-testid={`route-hours-clear-${route.route_code}`}>24×7</button>
        <button type="button" disabled={busy} className="btn-primary px-2 py-0.5" onClick={save} data-testid={`route-hours-save-${route.route_code}`}>Save hours</button>
      </div>
    </div>
  );
}
